import { Link, useParams } from "react-router-dom";
import { ArrowRight, Building2, CheckCircle2, Landmark, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumbs, COMPANY_NAME, PageHero, SERVICE_AREAS, SERVICE_PAGES, SITE_ORIGIN,
  SectionTag, SiteLayout, useLeadModal, useSEO,
} from "./shared";
import { DISTRICT_CONTENT } from "./districtContent";
import { NotFoundContent } from "./NotFound";

export default function DistrictDetail() {
  const { slug } = useParams();
  const { openQuote } = useLeadModal();
  const district = SERVICE_AREAS.find((d) => d.slug === slug);
  const content = district ? DISTRICT_CONTENT[slug] : null;
  const valid = Boolean(district && content);

  const title = valid
    ? `Fire Protection Services in ${district.name}, Tripura | SecureFlame`
    : "Service Area Not Found | SecureFlame Fire Systems";
  const description = valid
    ? `Fire alarm, hydrant, sprinkler, extinguisher and AMC services for buildings across ${district.name} district, Tripura — by SecureFlame Fire Systems, based in Agartala.`
    : "The service area page you're looking for could not be found on the SecureFlame Fire Systems website.";
  const jsonLd = valid
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: `Fire Protection Services in ${district.name}, Tripura`,
          description: content.intro,
          url: `${SITE_ORIGIN}/service-areas/${slug}`,
          provider: { "@type": "Organization", name: COMPANY_NAME, url: SITE_ORIGIN },
          areaServed: { "@type": "AdministrativeArea", name: `${district.name}, Tripura, India` },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_ORIGIN}/service-areas` },
            { "@type": "ListItem", position: 3, name: district.name, item: `${SITE_ORIGIN}/service-areas/${slug}` },
          ],
        },
      ]
    : [];

  useSEO(title, description, { jsonLd, noindex: !valid });

  if (!valid) {
    return (
      <SiteLayout>
        <NotFoundContent />
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Service Areas"
        title={`Fire Protection Services in ${district.name}`}
        subtitle={`District HQ: ${district.hq}. Fire alarm, hydrant, sprinkler, extinguisher, emergency lighting and AMC services for buildings across ${district.name}.`}
      />

      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <Breadcrumbs items={[{ label: "Service Areas", to: "/service-areas" }, { label: district.name }]} />

        <div className="mt-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div data-testid="district-intro">
              <SectionTag>About This Service Area</SectionTag>
              <p className="text-secondary/90 leading-relaxed text-base sm:text-lg">{content.intro}</p>
              {content.landmark && (
                <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground" data-testid="district-landmark">
                  <Landmark size={15} className="text-primary mt-0.5 shrink-0" /> {content.landmark}
                </p>
              )}
            </div>

            <div data-testid="district-buildings">
              <SectionTag>Buildings We Serve in {district.name}</SectionTag>
              <div className="flex flex-wrap gap-2.5">
                {content.buildings.map((b) => (
                  <span key={b} className="text-sm px-3.5 py-1.5 rounded-full border border-border text-secondary/90 bg-muted/50 flex items-center gap-1.5">
                    <Building2 size={13} className="text-primary" /> {b}
                  </span>
                ))}
              </div>
            </div>

            <div data-testid="district-services">
              <SectionTag>Services Available in {district.name}</SectionTag>
              <ul className="grid sm:grid-cols-2 gap-3">
                {SERVICE_PAGES.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="flex items-center gap-2.5 text-sm text-secondary/90 hover:text-primary transition-colors" data-testid={`district-service-link-${s.slug}`}>
                      <CheckCircle2 size={15} className="text-primary shrink-0" /> {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-sm border border-border p-7 bg-card">
              <MapPin className="text-primary mb-3" size={26} />
              <h3 className="font-heading font-bold text-lg text-secondary">Fire Safety Needs in {district.name}?</h3>
              <p className="mt-2 text-sm text-muted-foreground">Request a free site visit or a quotation for your property in {district.name} district.</p>
              <div className="mt-5 flex flex-col gap-3">
                <Button onClick={() => openQuote({ service_area: district.name })} className="w-full rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="district-detail-quote-btn">GET A QUOTE</Button>
                <Link to="/contact">
                  <Button variant="outline" className="w-full rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white" data-testid="district-detail-contact-btn">CONTACT US</Button>
                </Link>
              </div>
            </div>

            <div className="rounded-sm border border-border p-7 bg-card" data-testid="district-other-areas">
              <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-secondary mb-4">Other Service Areas</h3>
              <div className="flex flex-col gap-3">
                {SERVICE_AREAS.filter((d) => d.slug !== slug).slice(0, 5).map((d) => (
                  <Link key={d.slug} to={`/service-areas/${d.slug}`} className="flex items-center gap-2.5 text-sm text-secondary/90 hover:text-primary transition-colors" data-testid={`district-other-link-${d.slug}`}>
                    <MapPin size={15} className="text-primary shrink-0" /> {d.name}
                  </Link>
                ))}
                <Link to="/service-areas" className="text-sm font-semibold text-primary hover:underline mt-1" data-testid="district-all-areas-link">View All Districts</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Serving Every District of Tripura</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">From Agartala to every corner of the state — SecureFlame provides complete fire protection engineering wherever you need it.</p>
          <Link to="/service-areas">
            <Button size="lg" className="mt-6 rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2" data-testid="district-detail-all-areas-btn">VIEW ALL SERVICE AREAS <ArrowRight size={16} /></Button>
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
